import { useCallback, useEffect, useState } from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useAuth from 'hooks/useAuth';
import { useSelectedContent } from '@/contexts/SelectedContentContext';
import { getCompetitionLogo } from '@/utils/utils';

// Logo of the competition (by its name, the initial as at login when there's none) with the name and its season below
function CompetitionLabel({ competition }) {
  const logo = getCompetitionLogo(competition.name);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
      {logo ? (
        <Box component="img" src={logo} alt="" sx={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0 }} />
      ) : (
        <Avatar sx={{ width: 28, height: 28, fontSize: '0.85rem', flexShrink: 0 }}>{competition.name?.charAt(0).toUpperCase()}</Avatar>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.2 }} noWrap>{competition.name}</Typography>
        {competition.season && (
          <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.2 }} color="text.secondary" noWrap>{competition.season}</Typography>
        )}
      </Box>
    </Box>
  );
}

export default function CompetitionSelector() {
  const { user } = useAuth();
  const { setSelectedCompetition } = useSelectedContent();
  const competitions = Array.isArray(user?.competitions) ? user.competitions : [];
  const options = competitions.map((competition, index) => ({
    competition,
    value: `competition-${index}`
  }));
  const [selectedCompetitionId, setSelectedCompetitionId] = useState('');

  const applyCompetition = useCallback((competition) => {
    setSelectedCompetition(competition);
  }, [setSelectedCompetition]);

  useEffect(() => {
    if (options.length > 0) {
      const defaultIndex = competitions.findIndex((competition) => competition.is_default === true);
      const selectedIndex = defaultIndex >= 0 ? defaultIndex : 0;
      setSelectedCompetitionId(options[selectedIndex].value);
      applyCompetition(competitions[selectedIndex]);
    } else {
      setSelectedCompetitionId('');
      setSelectedCompetition(null);
    }
  }, [applyCompetition, user?.competitions, setSelectedCompetition]);

  if (options.length === 0) return null;

  return (
    <FormControl fullWidth size="small">
      <InputLabel id="drawer-competition-label">Competition</InputLabel>
      <Select
        labelId="drawer-competition-label"
        id="drawer-competition"
        value={selectedCompetitionId}
        label="Competition"
        renderValue={(value) => {
          const selected = options.find((option) => option.value === value);
          return selected && <CompetitionLabel competition={selected.competition} />;
        }}
        onChange={(event) => {
          const selectedOption = options.find((option) => option.value === event.target.value);
          setSelectedCompetitionId(event.target.value);
          applyCompetition(selectedOption?.competition ?? null);
        }}
      >
        {options.map(({ competition, value }) => (
          <MenuItem key={value} value={value}>
            <CompetitionLabel competition={competition} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
